"use client";

import { useRef } from "react";
import Image from "next/image";
import PageContent from "@/components/page-content";
import SectionHeader from "@/components/landing/section-header";
import { Globe2 } from "lucide-react";
import { Demos } from './demos';

type Demo = {
  title: string;
  image: string;
  prompt: string;
  video?: string;
};

const demos: Demo[] = [
  {
    title: "Autonomous driving",
    image: "https://depot.moondream.ai/seg/pedestrians.jpeg",
    video: "https://depot.moondream.ai/seg/pedestrians-web.mp4",
    prompt: "\"pedestrians in the crosswalk\"",
  },
  {
    title: "Sports / Media",
    image: "https://depot.moondream.ai/seg/basketball.jpeg",
    video: "https://depot.moondream.ai/seg/basketball-web.mp4",
    prompt: "\"person shooting the ball\"",
  },
  {
    title: "Aerial visual intelligence",
    image: "https://depot.moondream.ai/seg/aerial.jpeg",
    video: "https://depot.moondream.ai/seg/aerial-web.mp4",
    prompt: "\"white cars\"",
  },
  // {
  //   title: "Robotics / manipulation",
  //   image: "https://deifkwefumgah.cloudfront.net/shadcnblocks/block/lummi/random7.jpeg",
  //   prompt: "“the red mug” or “the screwdriver next to the box”",
  //   detail: "Tabletop scene with multiple objects.",
  // },
  // {
  //   title: "Satellite / aerial imagery",
  //   image: "https://deifkwefumgah.cloudfront.net/shadcnblocks/block/lummi/random4.jpeg",
  //   prompt: "“buildings”, “roads”, or “water”",
  //   detail: "Mixed urban and rural area from overhead.",
  // },
] satisfies Demo[];

function DemoCard({ demo }: { demo: Demo }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const handleEnter = () => {
    if (demo.video && videoRef.current) {
      videoRef.current.play().catch(() => { });
    }
  };

  const handleLeave = () => {
    if (demo.video && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      className="group rounded-2xl border border-border/70 bg-card/60 shadow-sm transition hover:-translate-y-1 hover:shadow-md"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <div className="relative overflow-hidden rounded-t-2xl aspect-[4/3]">
        {demo.video && (
          <video
            ref={videoRef}
            muted
            playsInline
            className="absolute inset-0 h-full w-full object-cover object-center opacity-0 transition duration-300 group-hover:opacity-100"
            poster={demo.image}
          >
            <source src={demo.video} type="video/mp4" />
          </video>
        )}
        <Image
          src={demo.image}
          alt={demo.title}
          width={640}
          height={400}
          className={
            demo.video
              ? "h-full w-full object-cover object-center transition duration-300 group-hover:opacity-0"
              : "h-full w-full object-cover object-center transition duration-300 group-hover:scale-105"
          }
        />
      </div>
      <div className="flex flex-col gap-1.5 px-5 py-4">
        <h3 className="text-lg font-semibold">{demo.title}</h3>
        <p className="text-sm text-muted-foreground">
          Prompt: <span className="font-medium text-foreground">{demo.prompt}</span>
        </p>
      </div>
    </div>
  );
}

export function RealWorldDemos() {
  return (
    <PageContent>
      <SectionHeader
        sectionIcon={<Globe2 className="size-4" />}
        section="Real-world demos"
        heading="Pixel Perfect Prompting"
        description="Same API, endless applications. See Segmentation across different domains."
        className="pb-4"
      />
      <div className="flex flex-col gap-4">
        <Demos />
        {/* Bottom row of demos hidden but kept in code */}
        {false && (
          <div className="pt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {demos.map((demo) => (
              <DemoCard key={demo.title} demo={demo} />
            ))}
          </div>
        )}
      </div>
    </PageContent >
  );
}
